import React, { useState, useEffect, useMemo } from "react";
import styles from "./Workflow.module.scss";
import CodeSnippet from "./LocalComponents/CodeSnippet";
import Card from "./LocalComponents/Card";
import WorkflowModal from "./LocalComponents/WorkflowModal";
import { userApi } from "../../../Utils/API";
import { GRAPHQL_PROXY } from "../../../Utils/Endpoints";
import loadingGif from "../../../Assets/Animations/loading.gif";

const Workflow = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  async function getWorkflowData() {
    const query = `
    query MyQuery {
      workflows(orderBy: {publishedUtc: DESC}) {
        displayText
        list {
          contentItems {
            ... on WorkflowStep {
              stepTitle
              endpoint {
                contentItems {
                  displayText
                  ... on Endpoints {
                    displayText
                    hTTPMethod {
                      termContentItems {
                        displayText
                      }
                    }
                    endpointURL
                  }
                }
              }
              stepDescription {
                html
              }
              codeSnippet
              displayText
            }
          }
        }
        description
      }
    }
    `;

    const token_type = localStorage.getItem("token_type");
    const access_token = localStorage.getItem("access_token");

    // if (!token_type || !access_token) {
    //   throw new Error("Authorization token not found.");
    // }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${token_type} ${access_token}`,
      },
    };

    const body = { query };

    try {
      const resp = await userApi.post(GRAPHQL_PROXY, body, config);
      if (resp.data && resp.data.data && resp.data.data.workflows) {
        setWorkflows(resp.data.data.workflows);
      }
    } catch (error) {
      console.error("Error fetching workflow data:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getWorkflowData();
  }, []);

  const openModal = (workflow, icon) => {
    setSelectedWorkflow(workflow);
    setSelectedIcon(icon);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedWorkflow(null);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add(styles.modalOpen);
    } else {
      document.body.classList.remove(styles.modalOpen);
    }
    return () => {
      document.body.classList.remove(styles.modalOpen);
    };
  }, [isModalOpen]);

  const heroWorkflow = workflows.find(
    (wf) => wf.displayText.includes("Placing New CSP Order")
  );

  const heroCodeSnippet = heroWorkflow
    ? heroWorkflow.list.contentItems
        .map((step) => {
          const normalizedSnippet = step.codeSnippet
            .replace(/[‘’]/g, "'")
            .replace(/\r\n/g, "\n")
            .replace(/\n\s*\\\s*\n/g, " \\\n");
          return `# ${step.stepTitle}\n${normalizedSnippet}`;
        })
        .join("\n\n")
    : "";

  const icons = [
    "api",
    "storage",
    "receipt_long",
    "dns",
    "cloud_queue",
    "support_agent",
    "gavel",
    "card_membership",
    "price_check",
  ];
  
  const groupedWorkflows = useMemo(() => {
    return workflows.reduce((acc, workflow) => {
      const parts = workflow.displayText.split(" - ");
      let category = "General";
      let cardTitle = workflow.displayText;

      if (parts.length > 1) {
        category = parts[0];
        cardTitle = parts.slice(1).join(" - ");
      }

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push({ ...workflow, cardTitle });
      return acc;
    }, {});
  }, [workflows]);

  if (isLoading) {
    return (
      <div className={styles.initialLoadingContainer}>
        <img src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.workflowContainer}>
        {heroWorkflow && (
          <div className={styles.workflowSection}>
            <div className={styles.workflowInfo}>
              <h2>{heroWorkflow.displayText.split("-")[1]}</h2>
              <p>{heroWorkflow.description}</p>
              <div className={styles.recipe}>
                <h5>In This Recipie</h5>
                <ul>
                  {heroWorkflow.list.contentItems.map((item, index) => (
                    <li key={index}>{`${index + 1}. ${item.stepTitle}`}</li>
                  ))}
                </ul>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => openModal(heroWorkflow, "api")}
              >
                Open Workflow
              </button>
            </div>
            <div className={styles.workflowCode}>
              <CodeSnippet language="curl" examples={{ curl: heroCodeSnippet }} />
            </div>
          </div>
        )}

        {Object.entries(groupedWorkflows).map(([category, workflowsInCategory], categoryIndex) => (
          <div key={categoryIndex} className={styles.subSection}>
            <div className={styles.subSectionHeader}>
              <h2>{`${category} ${workflowsInCategory.length > 1 ? 'Workflows' : 'Workflow'}`}</h2>
            </div>
            <div className={styles.cardGrid}>
              {workflowsInCategory.map((section, index) => (
                <Card
                  key={index}
                  icon={icons[index % icons.length]}
                  title={section.cardTitle}
                  description={section.description}
                  steps={section.list.contentItems}
                  onClick={() => openModal(section, icons[index % icons.length])}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {isModalOpen && (
        <WorkflowModal
          workflow={selectedWorkflow}
          icon={selectedIcon}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Workflow;